'use client';

import { ActionButtonWithDescription } from '@/shared/components/action-button-with-description';
import { ExternalPlayIcon } from '@/shared/components/primitives/svg/external-play-icon';
import { useTranslations } from 'next-intl';
import { cn } from '@ecom/helper-react-class-combine';
import { GameData } from '@/features/games/shared/types/game-data';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { isAlreadyRented } from '@/features/games/shared/helpers/is-already-rented';
import { isAlreadyPurchased } from '@/features/games/shared/helpers/is-already-purchased';
import { GameCategories } from '@/shared/constants/game-categories';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { getGameDownloadUrl } from '@/features/games/shared/helpers/get-game-download-url';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import Modal from '@/shared/components/overlay/modal';
import { PlayGameOverlay } from '@/features/games/internal/components/game-detail/play-game-overlay';
import { useHasTrialsLeft } from '@/features/games/internal/hooks/use-has-trials-left';
import { Loader } from '@/shared/components/primitives/loader';
import { emitGTMEvent } from '@/features/gtm/shared/helper/emit-gtm-event';

export const PlayGameButton = ({
  gameData,
  className,
}: {
  gameData: GameData;
  className?: string;
}) => {
  const t = useTranslations('GamePage');
  const { loading, authenticatedUser } = useUser();
  const isWTGamesApp = useIsWTGamesApp();

  const purchaseHistory = useGameStore((s) => s.purchaseHistory);
  const purchaseHistoryLoaded = useGameStore((s) => s.historyLoaded);
  const showOverlay = useOverlayStore((s) => s.showOverlay);

  // While we don’t even know auth/purchase-history readiness, show a loading button
  const preloading = loading || (authenticatedUser && !purchaseHistoryLoaded) || false;
  const isMember = authenticatedUser?.isWildMember || false;
  const userOwnsGame =
    isAlreadyPurchased(gameData.sku, purchaseHistory) ||
    isAlreadyRented(gameData.sku, purchaseHistory);
  const gameIsPartOfUnlimitedPlay =
    gameData.categories?.some((c) => c.name === GameCategories.UNLIMITED_PLAY) ?? false;
  const freeToPlay =
    gameData.categories?.some((c) => c.name === GameCategories.FREE_TO_PLAY) ?? false;
  const wildMemberPlay = isMember && gameIsPartOfUnlimitedPlay && !userOwnsGame;

  const { trialsLeft, loading: trialsLoading } = useHasTrialsLeft(gameData.sku);
  const needsTrialsCheck = !freeToPlay && !userOwnsGame && !wildMemberPlay;
  const canPlay = freeToPlay || wildMemberPlay || userOwnsGame || trialsLeft;
  const disabled = loading || (needsTrialsCheck && trialsLoading) || !canPlay;
  const isTrialDownload = !wildMemberPlay && !userOwnsGame && trialsLeft;

  let description;
  if (wildMemberPlay) {
    description = t('gameIncludedWithMembership');
  } else if (userOwnsGame) {
    description = t('downloadFullVersion');
  } else if (trialsLeft) {
    description = t('downloadFullVersionWithFreeTrial');
  } else {
    description = t('trailExpiredPurchaseFullVersion');
  }

  const title =
    gameData.productType === 'online' ? (
      <span className='flex items-center gap-1'>
        <ExternalPlayIcon /> {t('play')}
      </span>
    ) : (
      t('play')
    );

  const handleOpenLink = async (): Promise<void> => {
    if (isTrialDownload) {
      emitGTMEvent('GAME_TRIAL_DOWNLOAD_STARTED');
    }

    if (!isWTGamesApp) {
      showOverlay('play-game');
    }
    const downloadUrl = getGameDownloadUrl(gameData.sku, isWTGamesApp);
    window.location.assign(downloadUrl);
  };

  return (
    <div>
      <ActionButtonWithDescription
        title={<span className='text-lg'>{title}</span>}
        description={!preloading ? description : <Loader color='primary' size={24} />}
        variant='primary'
        isLoading={preloading}
        onClick={handleOpenLink}
        disabled={disabled}
        className={cn('min-h-20', className)}
      />
      <Modal modalKey='play-game'>
        <PlayGameOverlay />
      </Modal>
    </div>
  );
};
